import React, { useMemo, useState } from "react";
import { buildFacility360Metrics } from "../utils/facility360Metrics";

function formatMoney(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function KpiCard({
  label,
  value,
  detail,
  color = "#0f172a",
}) {
  return (
    <div
      style={{
        padding: 13,
        borderRadius: 14,
        border: "1px solid #e2e8f0",
        background: "#ffffff",
      }}
    >
      <div
        style={{
          color: "#64748b",
          fontSize: 10,
          fontWeight: 900,
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>

      <div
        style={{
          marginTop: 5,
          color,
          fontSize: 22,
          fontWeight: 950,
        }}
      >
        {value}
      </div>

      {detail ? (
        <div
          style={{
            marginTop: 4,
            color: "#64748b",
            fontSize: 10,
            fontWeight: 700,
          }}
        >
          {detail}
        </div>
      ) : null}
    </div>
  );
}

export default function Facility360KPI({
  facility,
  shifts = [],
  users = [],
  showMargin = false,
}) {
  const [period, setPeriod] = useState("90");

const sharedMetrics = useMemo(
  () =>
    buildFacility360Metrics({
      facility,
      shifts,
      users,
      periodDays: period,
    }),
  [facility, shifts, users, period]
);

const metrics = {
  totalShifts:
    sharedMetrics.totalShiftCount,

  completed:
    sharedMetrics.completedCount,

  open:
    sharedMetrics.openCount,

  active:
    sharedMetrics.activeCount,

  cancelled:
    sharedMetrics.cancelledCount,

  noShows:
    sharedMetrics.noShowCount,

  coverage:
    sharedMetrics.openCount +
      sharedMetrics.activeCount +
      sharedMetrics.completedCount >
    0
      ? sharedMetrics.coverage
      : null,

  completedHours:
    sharedMetrics.completedHours,

  workforce:
    sharedMetrics.workforceCount,

  readyWorkers:
    sharedMetrics.readyWorkerCount,

  compliantWorkers:
    sharedMetrics.compliantWorkerCount,

  roleCounts:
    sharedMetrics.completedShiftRoleCounts,

  revenue:
    sharedMetrics.financialSummary.revenue,

  payroll:
    sharedMetrics.financialSummary.payroll,

  margin:
    sharedMetrics.financialSummary.margin,
};

 const operationalHealth =
  metrics.coverage === null
    ? "No Data"
    : metrics.coverage >= 95 &&
      metrics.noShows === 0
    ? "Excellent"
    : metrics.coverage >= 85
    ? "Stable"
    : "Needs Attention";

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div
            style={{
              color: "#0f172a",
              fontSize: 20,
              fontWeight: 950,
            }}
          >
            Facility KPI Dashboard
          </div>

          <div
            style={{
              marginTop: 4,
              color: "#64748b",
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            Operational, workforce, attendance, and
            financial performance.
          </div>
        </div>

        <select
          value={period}
          onChange={(event) =>
            setPeriod(event.target.value)
          }
          style={{
            minHeight: 36,
            padding: "7px 10px",
            borderRadius: 10,
            border: "1px solid #cbd5e1",
            background: "#ffffff",
            color: "#0f172a",
            fontSize: 11,
            fontWeight: 850,
          }}
        >
          <option value="30">Last 30 Days</option>
          <option value="90">Last 90 Days</option>
          <option value="180">Last 180 Days</option>
          <option value="365">Last 12 Months</option>
          <option value="all">All History</option>
        </select>
      </div>

      <div
        style={{
          padding: 15,
          borderRadius: 16,
          color: "#ffffff",
          background:
            operationalHealth === "Excellent"
              ? "linear-gradient(135deg, #14532d, #16a34a)"
              : operationalHealth === "Stable"
              ? "linear-gradient(135deg, #1e3a8a, #2563eb)"
              : "linear-gradient(135deg, #7c2d12, #ea580c)",
        }}
      >
        <div
          style={{
            color: "#dbeafe",
            fontSize: 10,
            fontWeight: 900,
            textTransform: "uppercase",
          }}
        >
          Operational Health
        </div>

        <div
          style={{
            marginTop: 5,
            fontSize: 24,
            fontWeight: 950,
          }}
        >
          {operationalHealth}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(2, minmax(0, 1fr))",
          gap: 9,
        }}
      >
     <KpiCard
  label="Coverage"
  value={
    metrics.coverage !== null
      ? `${metrics.coverage}%`
      : "—"
  }
  detail="Covered shifts versus remaining open demand"
  color="#1d4ed8"
/>

        <KpiCard
          label="Completed Shifts"
          value={metrics.completed}
          color="#166534"
        />

        <KpiCard
          label="Open Shifts"
          value={metrics.open}
          color={
            metrics.open > 0 ? "#b45309" : "#166534"
          }
        />

        <KpiCard
          label="Completed Hours"
          value={metrics.completedHours.toFixed(1)}
          color="#7c3aed"
        />

        <KpiCard
          label="No-Shows"
          value={metrics.noShows}
          color={
            metrics.noShows > 0
              ? "#b91c1c"
              : "#166534"
          }
        />

        <KpiCard
          label="Cancellations"
          value={metrics.cancelled}
          color={
            metrics.cancelled > 0
              ? "#b45309"
              : "#166534"
          }
        />
      </div>

      <div
        style={{
          padding: 14,
          borderRadius: 16,
          border: "1px solid #dbeafe",
          background: "#eff6ff",
        }}
      >
        <div
          style={{
            color: "#1e3a8a",
            fontSize: 14,
            fontWeight: 950,
          }}
        >
          Workforce Health
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(3, minmax(0, 1fr))",
            gap: 8,
            marginTop: 10,
          }}
        >
          <KpiCard
            label="Workers"
            value={metrics.workforce}
          />

          <KpiCard
            label="Ready"
            value={metrics.readyWorkers}
            color="#166534"
          />

          <KpiCard
            label="Compliant"
            value={metrics.compliantWorkers}
            color="#7c3aed"
          />
        </div>
      </div>

      <div
        style={{
          padding: 14,
          borderRadius: 16,
          border: "1px solid #e2e8f0",
          background: "#f8fafc",
        }}
      >
        <div
          style={{
            color: "#0f172a",
            fontSize: 14,
            fontWeight: 950,
          }}
        >
          Financial Performance
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              showMargin
                ? "repeat(3, minmax(0, 1fr))"
                : "repeat(2, minmax(0, 1fr))",
            gap: 8,
            marginTop: 10,
          }}
        >
          <KpiCard
            label="Revenue"
            value={formatMoney(metrics.revenue)}
            color="#166534"
          />

          <KpiCard
            label="Payroll"
            value={formatMoney(metrics.payroll)}
            color="#1d4ed8"
          />

          {showMargin ? (
            <KpiCard
              label="Gross Margin"
              value={formatMoney(metrics.margin)}
              color={
                metrics.margin >= 0
                  ? "#7c3aed"
                  : "#b91c1c"
              }
            />
          ) : null}
        </div>
      </div>

      <div
        style={{
          padding: 14,
          borderRadius: 16,
          border: "1px solid #e2e8f0",
          background: "#ffffff",
        }}
      >
        <div
          style={{
            color: "#0f172a",
            fontSize: 14,
            fontWeight: 950,
          }}
        >
          Completed Shifts by Role
        </div>

        {Object.keys(metrics.roleCounts).length ===
        0 ? (
          <div
            style={{
              marginTop: 10,
              color: "#64748b",
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            No completed shifts are available for this
            period.
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gap: 8,
              marginTop: 11,
            }}
          >
            {Object.entries(metrics.roleCounts)
              .sort((a, b) => b[1] - a[1])
              .map(([role, count]) => (
                <div
                  key={role}
                  style={{
                    display: "flex",
                    justifyContent:
                      "space-between",
                    alignItems: "center",
                    padding: "9px 11px",
                    borderRadius: 10,
                    background: "#f8fafc",
                    border:
                      "1px solid #e2e8f0",
                  }}
                >
                  <span
                    style={{
                      color: "#334155",
                      fontSize: 11,
                      fontWeight: 850,
                    }}
                  >
                    {role}
                  </span>

                  <span
                    style={{
                      color: "#1d4ed8",
                      fontSize: 13,
                      fontWeight: 950,
                    }}
                  >
                    {count}
                  </span>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}